import jwt_decode from 'jwt-decode';
import moment from 'moment';
import { httpGet, httpPost, httpPostUnauthenticated } from './api.service';
import { AuthRequest } from '../requestModels/authRequest';
import { ResponseTokens } from '../types/Tokens';
import { UserProfile } from '../types/UserProfile';
import { RegistrationRequest } from '../requestModels/registrationRequest';
import { SingleResult } from '../types/SingleResult';
import { BaseApiResponse } from '../types/BaseApiResponse';
import { AppConfigSSO } from '../constants/Constants';
import { HttpRequester as requester } from '../https/HttpRequester';

const localStorageKey_refreshToken = 'refreshToken';
const localStorageKey_accessToken = 'accessToken';
const localStorageKey_expires = 'expires';
const localStorageKey_permissions = 'permissions';
const localStorageKey_redirectAfterLogin = 'redirectAfterLogin';
const localStorageKey_userProfile = 'userProfile';

let currentRefreshRequest: Promise<void> | null = null;

const generateNonce = (length = 16): string => {
  const charset = AppConfigSSO.charset;
  let result = '';
  for (let i = 0; i < length; i++) {
    result += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return result;
};

const AuthService = {
  isRefreshing: false,
  permissions: [] as string[],

  loginWithSSO(): void {
    const nonce = generateNonce();
    sessionStorage.setItem('auth_nonce', nonce);

    const authUrl =
      `https://${AppConfigSSO.domain}/authorize?` +
      `response_type=id_token token&` +
      `client_id=${AppConfigSSO.clientId}&` +
      `redirect_uri=${encodeURIComponent(AppConfigSSO.redirectUri ?? '')}&` +
      `scope=openid profile email&` +
      `nonce=${nonce}`;

    window.location.href = authUrl;
  },

  async handleSSOCallback(): Promise<boolean> {
    // grab tokens out of the URL (hash or search)
    const hash = window.location.hash || window.location.search.replace('?', '#');
    const params = new URLSearchParams(hash.replace(/^#/, ''));

    const idToken = params.get('id_token');
    if (!idToken) {
      return false;
    }

    // validate and remove the nonce
    sessionStorage.removeItem('auth_nonce');

    // decode the ID token to extract the email
    let payload: UserProfile | null = null;
    try {
      payload = jwt_decode<UserProfile>(idToken);
    } catch (err) {
      console.error('Failed to decode id_token:', err);
      return false;
    }

    const email = payload?.email;
    if (!email) {
      console.error('No email in ID token payload');
      return false;
    }

    // call normal authenticate endpoint with just the email the backend should detect that password is empty and trust the SSO ID token flow to issue a new app token.
    const authRequest: AuthRequest = { token: idToken };
    const res = await this.authenticate(authRequest);

    if (res.status !== 200 || !res.data) {
      console.error('Backend /api/auth failed for SSO login', res);
      return false;
    }

    // on success, .authenticate() call already sets refreshToken, accessToken, expires, permissions, and redirects to redirectAfterLogin.
    return true;
  },

  async isAuthenticated(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.getAccessToken().then((x) => resolve(x !== ''));
    });
  },

  decodeAccessToken(accessToken: string): UserProfile | null {
    const user: UserProfile = jwt_decode(accessToken);

    return user;
  },

  createUserProfile(data: any): UserProfile | null {
    if (!data) return null;

    const decodedProfile = data.accessToken ? this.decodeAccessToken(data.accessToken) : null;

    return {
      UserId: data.UserId ?? data.userId ?? decodedProfile?.UserId ?? '',
      email: data.email ?? decodedProfile?.email ?? '',
      given_name: data.given_name ?? data.firstName ?? decodedProfile?.given_name ?? '',
      firstName: data.firstName ?? decodedProfile?.firstName,
      lastName: data.lastName ?? decodedProfile?.lastName,
      exp: data.exp ?? decodedProfile?.exp ?? 0,
      isInternal: data.isInternal ?? decodedProfile?.isInternal ?? true,
    };
  },

  setUserProfile(userProfile: UserProfile | null) {
    if (!userProfile) {
      localStorage.removeItem(localStorageKey_userProfile);
      return;
    }

    localStorage.setItem(localStorageKey_userProfile, JSON.stringify(userProfile));
  },

  getStoredUserProfile(): UserProfile | null {
    const fromStorage = localStorage.getItem(localStorageKey_userProfile);
    if (!fromStorage) return null;

    try {
      return JSON.parse(fromStorage) as UserProfile;
    } catch (err) {
      localStorage.removeItem(localStorageKey_userProfile);
      return null;
    }
  },

  async getUserProfile(): Promise<UserProfile | null> {
    const storedProfile = this.getStoredUserProfile();
    if (storedProfile) return storedProfile;

    const accessToken = await AuthService.getAccessToken();
    if (accessToken === '') return null;

    return this.decodeAccessToken(accessToken);
  },

  async getAccessToken(): Promise<string> {
    let retVal = this.getAccessTokenFromQueryString();
    if (retVal) {
      localStorage.setItem(localStorageKey_accessToken, retVal);

      const url = new URL(window.location.href);
      url.searchParams.delete('token');

      window.location.href = url.toString();
    } else {
      retVal = localStorage.getItem(localStorageKey_accessToken);

      if (retVal) {
        const refreshToken = this.getRefreshToken();
        const expiresSeconds = this.getExpiresSeconds();

        // 10 seconds before the token times out, refresh the tokens
        const needsToRefresh = refreshToken && expiresSeconds !== undefined && expiresSeconds < 50;

        if (needsToRefresh) {
          try {
            await AuthService.refreshTokens();
            retVal = localStorage.getItem(localStorageKey_accessToken);
          } catch (e) {
            this.logout(); // redirect to login
          }
        }
      }
    }

    return retVal || '';
  },

  getAccessTokenFromQueryString(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get('token');
  },

  async authenticate(request: AuthRequest): Promise<ResponseTokens> {
    // building a payload that contains only the fields we need
    const payload: Record<string, string> = {};
    if (request.token) {
      payload.token = request.token;
    } else {
      payload.email = request.email ?? '';
      payload.password = request.password ?? '';
    }

    return await httpPostUnauthenticated<ResponseTokens>('api/auth', payload).then(async (res) => {
      if (res.status === 200 && res.data) {
        // same as before: store tokens, expiry, permissions
        localStorage.setItem(localStorageKey_refreshToken, res.data.refreshToken);
        localStorage.setItem(localStorageKey_accessToken, res.data.accessToken);
        localStorage.setItem(localStorageKey_expires, res.data.expires);
        const userProfile = this.createUserProfile(res.data);
        this.setUserProfile(userProfile);

        const perms = await httpGet<BaseApiResponse<string[]>>('api/auth/mypermissions');
        this.permissions = perms.data;
        localStorage.setItem(localStorageKey_permissions, JSON.stringify(perms.data));

        let redirect = localStorage.getItem(localStorageKey_redirectAfterLogin) || '/dashboard';
        if (userProfile?.isInternal && redirect.startsWith('/visits')) {
          redirect = '/dashboard';
        }
        localStorage.removeItem(localStorageKey_redirectAfterLogin);
        window.location.href = redirect;
      }
      return res;
    });
  },

  async refreshTokens(): Promise<void> {
    if (this.isRefreshing && currentRefreshRequest) return currentRefreshRequest;

    const refreshRequest = new Promise<void>((resolve) => {
      const accessToken = localStorage.getItem(localStorageKey_accessToken);

      if (!accessToken) resolve();
      else {
        this.isRefreshing = true;
        httpPostUnauthenticated<ResponseTokens>('api/auth/refresh', { refreshToken: this.getRefreshToken(), accessToken: accessToken }).then(
          async (res) => {
            if (res.status === 200 && res?.data) {
              this.setRefreshToken(res.data.refreshToken);
              this.setAccessToken(res.data.accessToken);
              this.setExpires(res.data.expires);
              this.setUserProfile(this.createUserProfile(res.data));

              await httpGet<BaseApiResponse<string[]>>('api/auth/mypermissions').then((x) => {
                this.setPermissions(x.data);
              });
            } else {
              this.logout();
            }

            this.isRefreshing = false;

            resolve();
          },
        );
      }
    });

    currentRefreshRequest = refreshRequest;
    return refreshRequest;
  },

  logout(): void {
    localStorage.removeItem(localStorageKey_accessToken);
    localStorage.removeItem(localStorageKey_expires);
    localStorage.removeItem(localStorageKey_refreshToken);
    localStorage.removeItem(localStorageKey_permissions);
    localStorage.removeItem(localStorageKey_userProfile);

    localStorage.setItem(localStorageKey_redirectAfterLogin, window.location.pathname + window.location.search);
    window.location.href = '/login';
  },

  setPermissions(permissions: string[] | undefined) {
    if (!permissions) {
      this.permissions = [];
      localStorage.removeItem(localStorageKey_permissions);
    } else {
      this.permissions = permissions;
      localStorage.setItem(localStorageKey_permissions, JSON.stringify(permissions));
      window.dispatchEvent(new Event('auth:permissions-loaded'));
    }
  },

  hasPermission(permission: string): boolean {
    if (!this.permissions.length) {
      const fromStorage = localStorage.getItem(localStorageKey_permissions);
      if (fromStorage) this.permissions = JSON.parse(fromStorage);
    }

    return this.permissions.indexOf(permission) > -1;
  },

  setRefreshToken(refreshToken: string | undefined) {
    if (refreshToken === undefined) localStorage.removeItem(localStorageKey_refreshToken);
    else localStorage.setItem(localStorageKey_refreshToken, refreshToken);
  },

  getRefreshToken(): string {
    return localStorage.getItem(localStorageKey_refreshToken) || '';
  },

  setExpires(expires: string | undefined): void {
    if (expires === undefined) localStorage.removeItem(localStorageKey_expires);
    else localStorage.setItem(localStorageKey_expires, expires);
  },

  getExpiresSeconds(): number | undefined {
    const val = localStorage.getItem(localStorageKey_expires);
    if (!val) return undefined;

    const date = moment(val);
    if (!date.isValid()) return undefined;

    return (date.toDate().getTime() - Date.now()) / 1000;
  },

  setAccessToken(accessToken: string | undefined) {
    if (accessToken === undefined) localStorage.removeItem(localStorageKey_accessToken);
    else localStorage.setItem(localStorageKey_accessToken, accessToken);
  },

  async register(registrationRequest: RegistrationRequest): Promise<SingleResult<object>> {
    return await httpPost<SingleResult<object>>(`api/auth/register`, registrationRequest);
  },
  async resetPassword(email: string): Promise<any> {
    // backend expects raw email string with Content-Type: text/plain
    return await requester.postPlainText('api/user/reset-password', email);
  },
};

export default AuthService;
